import { createElement } from 'react';
import { EmptyCircle } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import type { MarketThread } from '@/lib/notifications/group-by-market';
import { ICON_TONE_CLASSES, NOTIFICATION_TYPE_META } from '@/lib/notifications/notification-types';
import type { NotificationResponseDto } from '@/lib/models';

export interface NotificationThreadProps {
  thread: MarketThread;
  /** Fired when a sub-row is activated — opens its detail pop-up and marks it read. */
  onItemClick: (notification: NotificationResponseDto) => void;
}

/**
 * A market's threaded updates in the drawer: one card headed by the market
 * image + name and an update-count pill, with each related notification
 * (fill, cancel, resolution, …) as a compact sub-row carrying its own tinted
 * type icon, timestamp, read state, and click-through to the standard detail
 * pop-up. Exists so lifecycle events of the same market/order read as one
 * story instead of scattered, seemingly unrelated cards.
 */
export function NotificationThread({ thread, onItemClick }: NotificationThreadProps) {
  const hasUnread = thread.items.some((n) => !n.read);

  return (
    <li>
      <div
        data-testid="notification-thread"
        data-unread={hasUnread}
        className="relative rounded-card border border-border bg-surface px-3 py-3"
      >
        <div className="flex items-center gap-3 px-1 pb-2">
          <EmptyCircle src={thread.imageUrl} alt="" className="size-10 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">
            {thread.marketName ?? 'Market updates'}
          </span>
          <span className="shrink-0 rounded-pill bg-surface-muted px-2 py-0.5 text-[11px] font-semibold text-muted">
            {thread.items.length} updates
          </span>
        </div>
        {hasUnread && (
          <span
            data-testid="unread-dot"
            aria-label="Unread"
            className="absolute right-2 top-2 block size-3 rounded-full bg-danger ring-2 ring-surface"
          />
        )}

        <ul className="flex flex-col gap-1">
          {thread.items.map((notification) => {
            const meta = NOTIFICATION_TYPE_META[notification.type];
            return (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => onItemClick(notification)}
                  data-testid="notification-thread-item"
                  data-unread={!notification.read}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                    notification.read ? 'hover:bg-surface-muted' : 'bg-accent/10 hover:bg-accent/15',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-7 shrink-0 items-center justify-center rounded-full',
                      ICON_TONE_CLASSES[meta?.tone ?? 'muted'],
                    )}
                  >
                    {/* createElement keeps the lucide icon reference opaque to react-hooks/static-components. */}
                    {meta?.icon && createElement(meta.icon, { className: 'size-3.5' })}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-sm',
                      notification.read ? 'font-medium text-primary' : 'font-semibold text-primary',
                    )}
                  >
                    {notification.title}
                  </span>
                  <time className="shrink-0 text-xs text-muted" dateTime={notification.createdAt}>
                    {formatNotificationTime(notification.createdAt)}
                  </time>
                  {!notification.read && (
                    <span aria-label="Unread" className="block size-2 shrink-0 rounded-full bg-danger" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
}
